import React from "react";
import { Star } from "lucide-react";

/**
 * Csillag értékelés – megjelenítő és/vagy interaktív.
 * props:
 *   value: number — jelenlegi érték (float 0..5, pl. 4.3).
 *   onChange: (n) => void — ha megadva, interaktív (klikkelhető), 1..5 int-tel hív.
 *   size: 'xs' | 'sm' | 'md' | 'lg' — ikon méret.
 *   color: hex – csillag szín (default electric).
 *   testid: adat-testid prefix — "-{i}" indexeléssel.
 */
export function StarRating({ value = 0, onChange, size = "md", color = "#2563EB", testid }) {
  const [hover, setHover] = React.useState(null);
  const interactive = typeof onChange === "function";
  const shown = hover != null ? hover : value;

  const sizeCls = {
    xs: "w-3 h-3",
    sm: "w-3.5 h-3.5",
    md: "w-5 h-5",
    lg: "w-7 h-7",
  }[size] || "w-5 h-5";

  return (
    <div
      className={`inline-flex items-center gap-0.5 ${interactive ? "cursor-pointer" : ""}`}
      onMouseLeave={() => interactive && setHover(null)}
      role={interactive ? "radiogroup" : "img"}
      aria-label={`${Number(shown).toFixed(1)} / 5 csillag`}
    >
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = shown >= i;
        const half = !filled && shown >= i - 0.5;
        const commonCls = `p-0.5 leading-none ${interactive ? "hover:scale-110" : ""} transition-transform`;
        const commonStyle = { color: filled || half ? color : "#3f4756" };
        const icon = (
          <Star
            className={sizeCls}
            fill={filled ? "currentColor" : half ? "url(#half-star)" : "none"}
            strokeWidth={1.5}
          />
        );
        if (!interactive) {
          // Display mode: <span> a nested-button HTML hiba elkerülésére
          return (
            <span
              key={i}
              data-testid={testid ? `${testid}-${i}` : undefined}
              className={commonCls}
              style={commonStyle}
              aria-hidden="true"
            >
              {icon}
            </span>
          );
        }
        return (
          <button
            key={i}
            type="button"
            data-testid={testid ? `${testid}-${i}` : undefined}
            onMouseEnter={() => setHover(i)}
            onClick={() => onChange(i)}
            className={`${commonCls} disabled:cursor-default`}
            style={commonStyle}
            aria-checked={value === i ? "true" : "false"}
            role="radio"
          >
            {icon}
          </button>
        );
      })}
      {/* Fél csillag színátmenete */}
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          <linearGradient id="half-star" x1="0" x2="1" y1="0" y2="0">
            <stop offset="50%" stopColor={color} />
            <stop offset="50%" stopColor="transparent" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}
